import HyBase, { RequestOperation, type Action, type Options, type Actions } from './Base'
import type HyModel from './Model'

import {
  defaultsDeep,
  each,
  filter,
  find,
  findIndex,
  get,
  has,
  map,
  max,
  merge,
  method,
  set,
  size,
  toSafeInteger,
  unset,
  values
} from 'lodash'

/**
 * Used as a marker to indicate that pagination is not enabled.
 */
const NO_PAGE = null

/**
 * Used as a marker to indicate that a collection has paged through all results.
 */
const LAST_PAGE = 0
/**
 * Base collection class.
 */
class HyCollection extends HyBase {
  // Model store
  private _models!: Ref<HyModel[]>
  // Attribute storage
  private readonly _values!: Record<string, any>
  // private readonly _reference!: Record<string, any>
  // private readonly _errors!: Record<string, string[]>
  // private readonly _attributes!: Record<string, any>
  private readonly _page!: Ref<number | null>
  private readonly _registry!: Record<string, string>

  /**
   * Creates a new instance, called when using 'new'.
   *
   * @param  {Array}  [models]    Models to add to this collection.
   * @param  {Object} [options]   Extra options to set on this collection.
   */
  constructor(
    models: HyModel[] = [],
    options: Options = {},
    attributes: Record<string, any> = {}
  ) {
    // Call base constructor
    super(options)
    // Initialize instance properties
    this._models = ref([]) // Model store.
    this._values = reactive({}) // Active attribute state
    this._registry = reactive({}) // Active attribute state
    this._page = ref(NO_PAGE) // For pagination
    // Set all given attributes.
    this.set(defaultsDeep({}, attributes))
    // Add all given models (if any) to this collection. We explicitly ask
    // for the values here as it's common for some sources to be objects.
    if (models) {
      this.add(values(models))
    }
  }

  /**
   * Accessor to support Array.length semantics.
   */
  get length(): number {
    return this.size()
  }

  /**
   * @returns The default fields used in all collections. These will be
   * appended to the URL as query parameters when making GET requests.
   * They are useful for tracking meta information about the collection,
   * like page number, number of rows, etc.
   *  - `query` contains a string to be used when searching through a remote dataset
   */
  override getDefaultFields() {
    return {
      query: {}
    }
  }

  /**
   * By default, 'fetch', 'create', 'update', and 'delete' will
   * be created for a given model according to standard REST principles.
   *
   * @returns {Object<string, Action>}
   */
  getDefaultActions(): Actions {
    return {
      fetch: { method: 'GET', route: '{_remote_path}/' },
      create: { method: 'POST', route: '{_remote_path}/' },
      update: { method: 'PUT', route: '{_remote_path}/' }
    }
  }

  /**
   * @param {string} action The name of the action to look up
   *
   * @returns {Object} If the method is GET, returns all the built-in attributes
   * for the collection. Otherwise, returns an empty object.
   */
  override getQuery(action: Action): Record<string, string | number> {
    if (action.method === 'GET') {
      return this._values
    } else {
      return {}
    }
  }

  /**
   * @returns {Object} Parameters to use for replacement in route patterns.
   */
  override getRouteParameters(): Record<string, any> {
    const model = {
      _remote_path: this.getOption('remotePath') || `${useSnakeCase(this.getOption('remoteModel'))}s`
    }
    return merge(model, super.getRouteParameters(), this._values, {
      page: this._page.value,
    })
  }

  // Attributes

  /**
   * @return {*} The value of an attribute, or a given fallback if not set.
   */
  get(attribute: string, fallback?: any): any {
    return get(this._values, attribute, fallback)
  }

  /**
   * Sets an attribute's value, or an object of attributes.
   *
   * @param {string|Object} attribute
   * @param {*}             value
   */
  set(attribute: string | Record<string, any>, value?: any): void {
    // Handle groups of settings
    if (isPlainObject(attribute)) {
      each(attribute as Record<string, any>, (value, key): void => {
        this.set(key, value)
      })
      return
    }
    // Handle single values
    this._values[attribute as string] = value
  }

  /**
   * @returns {HyModel[]} An array containing all the models in this collection.
   */
  get models(): HyModel[] {
    return toValue(this._models)
  }

  // Properties

  /**
   * Returns the number of models in this collection.
   */
  size(): number {
    return size(this._models.value)
  }

  /**
   * Removes all errors from the models in this collection.
   */
  clearErrors(): void {
    each(this._models, method('clearErrors'))
  }

  /**
   * Resets model state, ie. `loading`, etc back to their initial states.
   */
  override clearState(): void {
    // TODO: clearState() ... this should be in HyBase
    // Vue.set(this, 'loading', false)
    // Vue.set(this, 'saving', false)
    // Vue.set(this, 'deleting', false)
    // Vue.set(this, 'fatal', false)
  }

  /**
   * Removes all models from this collection.
   */
  clearModels(): void {
    // Keep a copy of the models
    // let models: HyModel[] = this.models

    // Clear the model store
    this._models.value = []

    // // Notify each model that it has been removed from this collection.
    // each(models, (model: HyModel): void => {
    //     this.onRemove(model)
    // })
  }

  /**
   * Removes all models from this collection.
   */
  clear(): void {
    this.clearModels()
    this.clearState()
  }

  // Errors
  get errors(): Record<string, string | string[]> {
    throw new Error('Method not implemented.')
  }

  // Models

  /**
   * @return {HyModel} The class/constructor for this collection's model type.
   */
  model(): typeof HyModel {
    return this.getOption('model')
  }

  /**
   * Determines whether a given value is an instance of a model.
   *
   * @param  {*} candidate A model candidate
   *
   * @return {boolean} `true` if the given `model` is an instance of HyModel.
   */
  isModel(candidate: any): boolean {
    return (
      isObject(candidate) &&
      has(candidate, '_values') &&
      has(candidate, '_uid')
    )
  }

  /**
   * @param {string|function|Object} where
   *
   * @return {Model} The first model that matches the given criteria, or
   *                 `undefined` if none could be found.
   *
   * @see {@link https://lodash.com/docs/#find}
   */
  find(where: Record<string, string>): HyModel | undefined {
    return find(this._models.value, where) as HyModel | undefined
  }

  /**
   * @param {string|function|Object} where
   *
   * @return {boolean} Whether a model with the given criteria exists in the collection
   *
   * @see {@link https://lodash.com/docs/#find}
   */
  exists(where: Record<string, string>): boolean {
    return !!this.find(where)
  }

  /**
   * @return {Boolean} true if this collection has the model in its registry.
   */
  hasModelInRegistry(model: HyModel): boolean {
    return has(this._registry, model._uid)
  }

  /**
   * Create a new model of this collection's model type.
   *
   * @param {Object} attributes
   *
   * @returns {HyModel} A new instance of this collection's model.
   */
  createModel(attributes: Record<string, any>): Record<string, any> {
    return new (this.model())(attributes)
  }

  /**
   * Adds a model from the model registry.
   *
   * @param {HyModel} model
   */
  addModelToRegistry(model: HyModel): void {
    set(this._registry, model._uid, 1)
  }

  /**
   * Removes a model from the model registry.
   *
   * @param {Model} model
   */
  removeModelFromRegistry(model: HyModel): void {
    unset(this._registry, model._uid)
  }

  /**
   * @param {Object} response
   *
   * @returns {Array|null} Models from the response.
   */
  getModelsFromResponse(data: any): any {
    // Check if the response is an array
    if (isArray(data)) {
      return data
    }

    // Find the remote data in the response
    const remoteData = this.getOption('remoteData') || this.getOption('remotePath') || `${useSnakeCase(this.getOption('remoteModel'))}s`
    const models: unknown = data[remoteData]
    // An empty, non-array response indicates that we didn't intend to send
    // any models in the response. This means that the current models are
    // already up to date, as no changes are necessary.
    if (isNil(models) || models === '') {
      return null
    }
    // We're making an assumption here that paginated models are returned
    // within the "data" field of the response.
    // if (this.isPaginated()) {
    //     return get(models, 'data', models)
    // }
    return models
  }
  /**
   * Called when a model has been added to this collection.
   *
   * @param {HyModel} model
   */
  onAdd(model: HyModel): void {
    model.registerCollection(this)
    this.addModelToRegistry(model)
    // this.emit('add', { model })
  }

  /**
   * Adds a model to this collection.
   *
   * This method returns a single model if only one was given, but will return
   * an array of all added models if an array was given.
   *
   * @param {HyModel|Array|Object} model Adds a model instance or plain object,
   *                                   or an array of either, to this collection.
   *                                   A model instance will be created and
   *                                   returned if passed a plain object.
   *
   * @returns {HyModel|Array} The added model or array of added models.
   */
  add(model: HyModel[]): HyModel[]
  add(model?: HyModel | Partial<HyModel> | Record<string, any>): HyModel
  add(
    model?: HyModel | HyModel[] | Partial<HyModel> | Record<string, any>
  ): HyModel | HyModel[] | Partial<HyModel> | Record<string, any> | void {
    // If given an array, assume an array of models and add them all.
    if (isArray(model)) {
      return filter(map(model as HyModel[], this.add))
    }
    // Objects should be converted to model instances first, then added.
    if (isPlainObject(model)) {
      return this.add(
        this.createModel(model as Partial<HyModel> | Record<string, any>)
      )
    }
    // This is also just to catch a potential bug. All models should have
    // an auto id so this would indicate an unexpected state.
    if (!this.isModel(model)) {
      throw new Error('Expected a model, plain object, or array of either')
    }
    // Make sure we don't add the same model twice.
    if (this.hasModelInRegistry(model as HyModel)) {
      return
    }
    // Add the model instance to this collection.
    this._models.value.push(model as HyModel)
    // Run callback
    this.onAdd(model as HyModel)
    // We're assuming that the collection is not loading once a model is added.
    this.setFlag('loading', false)
    // Return model for chaining
    return model
  }

  /**
   * Returns the zero-based index of the given model in this collection.
   *
   * @see {@link https://lodash.com/docs/#findIndex}
   *
   * @return {number} the index of a model in this collection, or -1 if not found.
   */
  indexOf(model: HyModel): number {
    let filter: HyModel | { _uid: string } = model
    // Getting the index of a model instance can be optimised.
    if (this.isModel(filter)) {
      // Constant time check, if the registry doesn't have a record of
      // the model, we know it's not in the collection.
      if (!has(this._registry, model._uid)) {
        return -1
      }
      // There is no need to filter on the entire object, because the
      // unique ID of the model is all we need to identify it.
      filter = { _uid: model._uid }
    }
    return findIndex(this._models.value, filter)
  }

  /**
   * Called when a model has been removed from this collection.
   *
   * @param {Model} model
   */
  onRemove(model: HyModel): void {
    model.unregisterCollection(this)
    this.removeModelFromRegistry(model)
    // this.emit('remove', { model })
  }

  /**
   * Removes a model at a given index.
   *
   * @param  {number} index

    * @returns {Model} The model that was removed, or `undefined` if invalid.
    * @throws  {Error} If a model could not be found at the given index.
    */
  _removeModelAtIndex(index: number): HyModel | undefined {
    // Sanity check
    if (index < 0) { return }
    // Remove model
    const model: HyModel = get(this._models.value, index)
    this._models.value.splice(index, 1)
    // Callback
    this.onRemove(model)
    // Return removed model
    return model
  }

  /**
   * Removes a `Model` from this collection.
   *
   * @param  {Model} model
   *
   * @return {Model}
   */
  _removeModel(model: HyModel): HyModel | undefined {
    return this._removeModelAtIndex(this.indexOf(model))
  }


  /**
   * Removes the given model from this collection.
   *
   * @param  {Model|Object|Array} model Model to remove, which can be a `Model`
   *                                    instance, an object to filter by,
   *                                    a function to filter by, or an array
   *                                    of any of the above to remove multiple.
   *
   * @return {Model|Model[]} The deleted model or an array of models if a filter
   *                         or array type was given.
   *
   * @throws {Error} If the model is an invalid type.
   */
  remove(model: HyModel): HyModel
  remove(model: HyModel[] | Partial<HyModel> | ((model: HyModel) => boolean)): HyModel[]
  remove(
    model: HyModel | HyModel[] | Partial<HyModel> | ((model: HyModel) => boolean)
  ): HyModel | HyModel[] | undefined {
    if (!model) {
      throw new Error('Expected function, object, array, or model to remove')
    }
    // Support using a predicate to remove all models it returns true for.
    // Alternatively support an object of values to filter by.
    if (isFunction(model) || isPlainObject(model)) {
      return this.remove(filter(this._models.value, model))
    }
    // Support removing multiple models at the same time if an array was
    // given. A model would otherwise always be an object so this is safe.
    if (isArray(model)) {
      return filter(map<HyModel, HyModel>(model, this.remove))
    }
    // This is just to catch a potential bug. All models should have
    // an auto id here so this would indicate an unexpected state.
    if (!this.isModel(model)) {
      throw new Error('Model to remove is not a valid model')
    }
    return this._removeModel(model as HyModel)
  }

  // HyModel Functions + Properties

  /**
   * @param attribute The attribute to pluck
   * 
   * @returns An array containing the values for the attribute in the collection's models
   */
  pluck(attribute: string) {
    return this._models.value.map((model: any) => model.get(attribute))
  }

  /**
   * @returns An array of objects representing the models stored in this collection
   */
  getObjects() {
    return this._models.value.map((model: HyModel) => model.attributes)
  }

  /**
   * Replaces all models in this collection with those provided. This is
   * effectively equivalent to `clear` and `add`, and will result in an empty
   * collection if no models were provided.
   *
   * @param {HyModel|HyModel[]} models Models to replace the current models with.
   */
  replace(models: HyModel | HyModel[]): void {
    this.clearModels()
    this.add(values(models))
  }

  // Pagination

  /**
   * Sets the page on this collection, enabling pagination. To disable
   * pagination on this collection, pass page as `null` or `undefined`.
   *
   * @param {number|boolean} [page] Page number, or `null` to disable.
   *
   * @returns {HyCollection} This collection.
   */
  page(page: number | boolean): this {
    // Disable pagination if a valid page wasn't provided.
    if (isNil(page)) {
      this._page.value = NO_PAGE
      // Page was provided, so we should either set the page or disable
      // pagination entirely if the page is `false`.
    } else {
      this._page.value = max([1, toSafeInteger(page)]) || null
    }
    // Return model for chaining
    return this
  }

  /**
   * @returns {integer|null} The page that this collection is on.
   */
  getPage(): number | null {
    return this._page.value
  }

  /**
   * @returns {boolean} Whether this collection is currently paginated.
   */
  isPaginated(): boolean {
    return this._page !== NO_PAGE
  }

  /**
   * @returns {boolean} Whether this collection is on the last page,
   *                    ie. there won't be more results that follow.
   */
  isLastPage(): boolean {
    return this._page.value === LAST_PAGE
  }

  /**
   * Responsible for adjusting the page and appending of models that were
   * received by a paginated fetch request.
   *
   * @param {HyModel[]} models
   */
  applyPagination(models: HyModel[]): void {
    // If no models were returned in the response we can assume that
    // we're now on the last page, and we should not continue.
    if (isEmpty(models)) {
      this._page.value = LAST_PAGE
      // Otherwise, there was at least one model, and we can safely
      // assume that we want to increment the page number.
    } else {
      this._page.value = (this._page.value as number) + 1
      this.add(models)
    }
  }

  // Fetching

  /**
   * Called when a fetch request was successful.
   *
   * @param {Object} response
   */
  onFetchSuccess(response: any): void {
    const data: any = response.data.value
    const models: any = this.getModelsFromResponse(data)
    // There is no sensible alternative to an array here, so anyting else
    // is considered an exception that indicates an unexpected state.
    // if (!isArray(models)) {
    //   throw this.createResponseError("No data in fetch response", response);
    // }
    // Append via pagination.
    // if (this.isPaginated()) {
    //   this.applyPagination(models)
    //   // Replace all current models with the fetched ones.
    // } else {
    this.replace(models)
    // }
    // Set flags
    this.setFlag('loading', false)
    this.setFlag('fatal', false)
    // Emit event
    // this.emit('fetch', { error: null })
  }

  /**
   * Called when a fetch request failed.
   *
   * @param {Error}  error
   */
  onFetchFailure(_error: any): void {
    this.clearErrors()
    // Set flags
    this.setFlag('loading', false)
    this.setFlag('fatal', true)
    // Emit event
    // this.emit('fetch', { error })
  }

  /**
   * Called before a fetch request is made.
   *
   * @returns {boolean|undefined} `false` if the request should not be made.
   */
  onFetch(): Promise<RequestOperation> {
    return new Promise((resolve): void => {
      // Don't fetch if there are no more results to be fetched.
      if (this.isPaginated() && this.isLastPage()) {
        return resolve(HyBase.REQUEST_SKIP)
      }
      // Because we're fetching new data, we can assume that this collection
      // is now loading. This allows the template to indicate a loading state.
      this.setFlag('loading', true)
      resolve(HyBase.REQUEST_CONTINUE)
      return
    })
  }

  // Saving

  reset(attributes: string | string[]): void {
    throw new Error('reset not implemented.')
  }

  getSaveData(formData: FormData): void {
    throw new Error('getSaveData not implemented.')
  }

  onSave(): Promise<RequestOperation> {
    throw new Error('onSave not implemented.')
  }

  onSaveSuccess(data: any): void {
    throw new Error('onSaveSuccess not implemented.')
  }

  onSaveFailure(error: any): void {
    throw new Error('onSaveFailure not implemented.')
  }

  // Deleting
  onDelete(): Promise<RequestOperation> {
    throw new Error('onDelete not implemented.')
  }

  onDeleteSuccess(response: Response): void {
    throw new Error('onDeleteSuccess not implemented.')
  }

  onDeleteFailure(error: any): void {
    throw new Error('onDeleteFailure not implemented.')
  }
}

export default HyCollection

export type Predicate<T = boolean> =
  | ((model: HyModel) => T)
  | string
  | Record<string, any>
  | HyModel
  | Partial<HyModel>
